import {ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Inject, OnInit, Type, ViewChild, ViewContainerRef,} from '@angular/core';

import {AcDialogRef, DIALOG_CONFIG, DialogConfig, DialogData} from './ac-dialog.models';
import {animate, style, transition, trigger} from '@angular/animations';

import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AcFormComponent} from '../ac-form/ac-form.component';
import {AcDialogHostComponent} from './ac-dialog-host/ac-dialog-host.component';
import {Subject} from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'ac-dialog',
    templateUrl: './ac-dialog.component.html',
    styleUrls: ['./ac-dialog.component.less'],
    animations: [
        trigger('dialogInOut', [
            transition(':enter', [
                style({opacity: 0}),
                animate('0.2s ease-in', style({opacity: 1}))
            ]),
            transition(':leave', [
                animate('0.1s ease-out', style({opacity: 0}))
            ])
        ])
    ],
    host: {
        tabindex: '0'
    }
})
export class AcDialogComponent implements OnInit {

    @HostBinding('@dialogInOut') dialogInOut;
    @ViewChild('dialog', {static: true}) dialog: ElementRef;
    forms: AcFormComponent[] = [];
    draggableInstance;
    allFormsAreInViewMode;
    dialogClosed = false;
    loading = true;
    @ViewChild('dialogContentHost', {read: ViewContainerRef, static: true}) private dialogContentHost: ViewContainerRef;
    private closeDialogSubject = new Subject();
    closeDialog$ = this.closeDialogSubject.asObservable().pipe(untilDestroyed(this));
    private dragStartSubject = new Subject();
    dragStart$ = this.dragStartSubject.asObservable().pipe(untilDestroyed(this));

    constructor(@Inject(DIALOG_CONFIG) public dialogConfig: DialogConfig,
                private acDialogRef: AcDialogRef,
                private acDialogHostComponent: AcDialogHostComponent,
                private elementRef: ElementRef,
                public cdRef: ChangeDetectorRef) {
        this.acDialogHostComponent.escape$
            .pipe(untilDestroyed(this))
            .subscribe((viewRefToClose) => {
                if (viewRefToClose !== this.acDialogRef.dialogHostView) {
                    return;
                }
                !dialogConfig.preventEscape && this.close();
            });
    }

    close(data?: DialogData) {
        this.closeDialogSubject.next(data || this.dialogConfig.dialogData);
    }

    cancel() {
        this.dialogConfig.onCancel && this.dialogConfig.onCancel(this.dialogConfig.dialogData);
    }

    ngOnInit(): void {
        this.elementRef.nativeElement.focus();
        setTimeout(() => this.loading = false);
    }

    ngAfterViewInit() {
        this.optionsChanged();
    }

    optionsChanged() {
        this.cdRef.detectChanges();
    }

    addForm(form: AcFormComponent) {
        this.forms.push(form);

        this.allFormsAreInViewMode = !this.dialogConfig.overrideSystemViewMode && !this.forms.some((acForm) => acForm.isViewMode === false);
    }

    createDialogContent(acDialogContentComponent?: Type<any>) {
        this.dialogContentHost.clear();
        this.dialogContentHost.createComponent(acDialogContentComponent);
    }

    onSubmit = () => {
        let formsAreValid = true;

        this.forms.forEach((form) => {
            form.formSettings.touched = true;
            form.handleValidation(true);
            formsAreValid = formsAreValid && form.formSettings.valid;
        });

        formsAreValid && this.submit();
    };

    ngOnDestroy() {
        !this.dialogConfig.submitButtonProcessing && this.cancel();
        this.dialogConfig.onClose && this.dialogConfig.onClose(this.dialogConfig.dialogData);
    }

    onDrag = () => this.dragStartSubject.next(null);

    private async submit() {
        this.dialogConfig.submitButtonProcessing = true;
        try {
            this.dialogConfig.preSubmit && await this.dialogConfig.preSubmit(this.dialogConfig.dialogData);
            await this.dialogConfig.onSubmit(this.dialogConfig.dialogData);
            this.close();
        } catch (err) {
            this.dialogConfig.submitButtonProcessing = false;
        }
    }
}
